import { z } from 'zod';
import { PermissionDefinitionSchema } from '../types/permission.js';

// The Prisma `HttpMethod` enum — and therefore the access-template permission
// rows — accepts all seven verbs. Reuse the canonical definition rather than a
// narrower copy, or an OPTIONS/HEAD tuple written by another writer is rejected
// on the way back in.
const PermissionEntrySchema = PermissionDefinitionSchema;

export const TemplateCreatedPayload = z.object({
    templateId: z.string().min(1),
    name: z.string().min(1),
    description: z.string().max(500).optional(),
    scopeMode: z.enum(['FIXED', 'WILDCARD']),
    tenantIds: z.array(z.string().min(1)),
    permissions: z.array(PermissionEntrySchema),
    createdByUserId: z.string().min(1).optional(),
    canBeEditedByTenantId: z.string().min(1).optional(),
});
export type TemplateCreatedPayload = z.infer<typeof TemplateCreatedPayload>;

export const TemplateUpdatedPayload = z.object({
    templateId: z.string().min(1),
    previousName: z.string().optional(),
    changes: z.object({
        name: z.string().min(1).optional(),
        description: z.string().max(500).nullable().optional(),
        tenantIds: z.array(z.string().min(1)).optional(),
        permissions: z.array(PermissionEntrySchema).optional(),
    }).refine((c) => Object.keys(c).length > 0, { message: 'At least one change is required' }),
});
export type TemplateUpdatedPayload = z.infer<typeof TemplateUpdatedPayload>;

export const TemplateStatusChangedPayload = z.object({
    templateId: z.string().min(1),
    status: z.enum(['ACTIVE', 'ARCHIVED']),
});
export type TemplateStatusChangedPayload = z.infer<typeof TemplateStatusChangedPayload>;

export const TemplateGrantedPayload = z.object({
    templateId: z.string().min(1),
    userIds: z.array(z.string().min(1)).min(1),
    tenantIds: z.array(z.string().min(1)).min(1),
});
export type TemplateGrantedPayload = z.infer<typeof TemplateGrantedPayload>;

export const TemplateRevokedPayload = z.object({
    templateId: z.string().min(1),
    userIds: z.array(z.string().min(1)).min(1),
    tenantIds: z.array(z.string().min(1)).min(1),
});
export type TemplateRevokedPayload = z.infer<typeof TemplateRevokedPayload>;

/** Cold-assign or remove one Wildcard role on one organization. No holders. */
export const TemplateAppliedToOrgPayload = z.object({
    templateId: z.string().min(1),
    tenantId: z.string().min(1),
});
export type TemplateAppliedToOrgPayload = z.infer<typeof TemplateAppliedToOrgPayload>;

export const TemplateRemovedFromOrgPayload = TemplateAppliedToOrgPayload;
export type TemplateRemovedFromOrgPayload = z.infer<typeof TemplateRemovedFromOrgPayload>;
